import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { AREAS_OF_INTEREST } from '../../../shared/constants/index.js';
import { getMedicalRoleOptions } from '../utils/medicalRoles';
import { MedicalRole, UserSex } from '../types';

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  sex: UserSex;
  hospitalAffiliation: string;
  yearsOfExperience: number;
  areasOfInterest: string[];
  medicalRole: MedicalRole;
  agreesToTerms: boolean;
}



export function RegisterPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    sex: 'male',
    hospitalAffiliation: '',
    yearsOfExperience: 0,
    areasOfInterest: [],
    medicalRole: 'specialist_doctor',
    agreesToTerms: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAreaOfInterestChange = (area: string) => {
    setFormData(prev => ({
      ...prev,
      areasOfInterest: prev.areasOfInterest.includes(area)
        ? prev.areasOfInterest.filter(s => s !== area)
        : [...prev.areasOfInterest, area]
    }));
  };

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Το email είναι υποχρεωτικό';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Μη έγκυρο email';
    }

    if (!formData.password) {
      newErrors.password = 'Ο κωδικός είναι υποχρεωτικός';
    } else {
      // Comprehensive password validation
      const passwordErrors: string[] = [];
      
      if (formData.password.length < 8) {
        passwordErrors.push('τουλάχιστον 8 χαρακτήρες');
      }
      
      if (!/[A-ZΆΈΉΊΌΎΏΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩ]/.test(formData.password)) {
        passwordErrors.push('έναν κεφαλαίο χαρακτήρα (A-Z ή Α-Ω)');
      }
      
      if (!/[a-zάέήίόύώαβγδεζηθικλμνξοπρστυφχψως]/.test(formData.password)) {
        passwordErrors.push('έναν μικρό χαρακτήρα (a-z ή α-ω)');
      }
      
      if (!/[0-9]/.test(formData.password)) {
        passwordErrors.push('έναν αριθμό (0-9)');
      }
      
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\|,.<>\/?]/.test(formData.password)) {
        passwordErrors.push('έναν ειδικό χαρακτήρα (!@#$%^&* κλπ.)');
      }
      
      if (passwordErrors.length > 0) {
        newErrors.password = `Ο κωδικός πρέπει να περιέχει ${passwordErrors.join(', ')}`;
      }
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Οι κωδικοί δεν ταιριάζουν';
    }

    if (!formData.firstName) {
      newErrors.firstName = 'Το όνομα είναι υποχρεωτικό';
    } else if (!/^[a-zA-ZάέήίόύώΆΈΉΊΌΎΏαβγδεζηθικλμνξοπρστυφχψωΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩς\s-]+$/.test(formData.firstName)) {
      newErrors.firstName = 'Το όνομα μπορεί να περιέχει μόνο γράμματα (ελληνικά ή αγγλικά)';
    }

    if (!formData.lastName) {
      newErrors.lastName = 'Το επώνυμο είναι υποχρεωτικό';
    } else if (!/^[a-zA-ZάέήίόύώΆΈΉΊΌΎΏαβγδεζηθικλμνξοπρστυφχψωΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩς\s-]+$/.test(formData.lastName)) {
      newErrors.lastName = 'Το επώνυμο μπορεί να περιέχει μόνο γράμματα (ελληνικά ή αγγλικά)';
    }

    if (!formData.sex) {
      newErrors.sex = 'Η επιλογή φύλου είναι υποχρεωτική';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Areas of interest are only mandatory for roles other than external_collaborator
    if (formData.medicalRole !== 'external_collaborator') {
      if (!formData.areasOfInterest || formData.areasOfInterest.length === 0) {
        newErrors.areasOfInterest = 'Πρέπει να επιλέξετε τουλάχιστον μία περιοχή ενδιαφέροντος';
      }
    }

    if (!formData.agreesToTerms) {
      newErrors.agreesToTerms = 'Πρέπει να αποδεχτείτε τους όρους χρήσης';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const { register } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isLoading) {
      return;
    }
    
    if (!validateStep2()) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        sex: formData.sex,
        hospitalAffiliation: formData.hospitalAffiliation,
        yearsOfExperience: formData.yearsOfExperience,
        areasOfInterest: formData.areasOfInterest,
        medicalRole: formData.medicalRole
      });

      // Check if email verification is required
      if (response?.emailVerificationRequired) {
        navigate('/email-verification-reminder');
      } else {
        navigate('/', { 
          state: { message: 'Η εγγραφή ολοκληρώθηκε επιτυχώς!' }
        });
      }
    } catch (error: any) {
      setErrors({ general: error.response?.data?.message || 'Σφάλμα κατά την εγγραφή' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">
            Εγγραφή στο Pathiety
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Κοινότητα Ελλήνων Παθολογοανατόμων
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-500'
              }`}>
                <span className="text-sm font-medium">1</span>
              </div>
              <div className={`flex-1 h-1 mx-2 ${
                currentStep >= 2 ? 'bg-blue-600' : 'bg-gray-300'
              }`}></div>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-500'
              }`}>
                <span className="text-sm font-medium">2</span>
              </div>
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-xs text-gray-500">Βασικά Στοιχεία</span>
              <span className="text-xs text-gray-500">Επαγγελματικά Στοιχεία</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {currentStep === 1 && (
              <>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email *
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`appearance-none block w-full px-3 py-2 border ${
                        errors.email ? 'border-red-300' : 'border-gray-300'
                      } rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                      placeholder="παθολογος@νοσοκομειο.gr"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                      Όνομα *
                    </label>
                    <div className="mt-1">
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`appearance-none block w-full px-3 py-2 border ${
                          errors.firstName ? 'border-red-300' : 'border-gray-300'
                        } rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                      Επώνυμο *
                    </label>
                    <div className="mt-1">
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`appearance-none block w-full px-3 py-2 border ${
                          errors.lastName ? 'border-red-300' : 'border-gray-300'
                        } rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Φύλο *
                  </label>
                  <div className="mt-2 space-y-2">
                    <label className="inline-flex items-center mr-6">
                      <input
                        type="radio"
                        name="sex"
                        value="male"
                        checked={formData.sex === 'male'}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Άνδρας</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="sex"
                        value="female"
                        checked={formData.sex === 'female'}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Γυναίκα</span>
                    </label>
                  </div>
                  {errors.sex && (
                    <p className="mt-1 text-sm text-red-600">{errors.sex}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Κωδικός Πρόσβασης *
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`appearance-none block w-full px-3 py-2 border ${
                        errors.password ? 'border-red-300' : 'border-gray-300'
                      } rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                      placeholder="Τουλάχιστον 8 χαρακτήρες"
                    />
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                    )}
                    {/* Password Requirements */}
                    <div className="mt-2 text-xs text-gray-600">
                      <p className="font-medium mb-1">Απαιτήσεις κωδικού:</p>
                      <div className="space-y-1">
                        <div className={`flex items-center ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-2">{formData.password.length >= 8 ? '✓' : '○'}</span>
                          Τουλάχιστον 8 χαρακτήρες
                        </div>
                        <div className={`flex items-center ${/[A-ZΆΈΉΊΌΎΏΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩ]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-2">{/[A-ZΆΈΉΊΌΎΏΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩ]/.test(formData.password) ? '✓' : '○'}</span>
                          Ένας κεφαλαίος χαρακτήρας (A-Z, Α-Ω)
                        </div>
                        <div className={`flex items-center ${/[a-zάέήίόύώαβγδεζηθικλμνξοπρστυφχψως]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-2">{/[a-zάέήίόύώαβγδεζηθικλμνξοπρστυφχψως]/.test(formData.password) ? '✓' : '○'}</span>
                          Ένας μικρός χαρακτήρας (a-z, α-ω)
                        </div>
                        <div className={`flex items-center ${/[0-9]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-2">{/[0-9]/.test(formData.password) ? '✓' : '○'}</span>
                          Ένας αριθμός (0-9)
                        </div>
                        <div className={`flex items-center ${/[!@#$%^&*()_+\-=\[\]{};':"\|,.<>\/?]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-2">{/[!@#$%^&*()_+\-=\[\]{};':"\|,.<>\/?]/.test(formData.password) ? '✓' : '○'}</span>
                          Ένας ειδικός χαρακτήρας (!@#$%^&* κλπ.)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Επιβεβαίωση Κωδικού *
                  </label>
                  <div className="mt-1">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`appearance-none block w-full px-3 py-2 border ${
                        errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                      } rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                    />
                    {errors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                    )}
                  </div>
                </div>

                <div>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Επόμενο
                  </button>
                </div>
              </>
            )}

            {currentStep === 2 && (
              <>

                <div>
                  <label htmlFor="medicalRole" className="block text-sm font-medium text-gray-700">
                    Βαθμίδα/Θέση *
                  </label>
                  <div className="mt-1">
                    <select
                      id="medicalRole"
                      name="medicalRole"
                      value={formData.medicalRole}
                      onChange={handleInputChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      {getMedicalRoleOptions(formData.sex).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="hospitalAffiliation" className="block text-sm font-medium text-gray-700">
                    Νοσοκομείο/Ίδρυμα
                  </label>
                  <div className="mt-1">
                    <input
                      id="hospitalAffiliation"
                      name="hospitalAffiliation"
                      type="text"
                      value={formData.hospitalAffiliation}
                      onChange={handleInputChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="π.χ. Γ.Ν. Αθηνών (προαιρετικό)"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="yearsOfExperience" className="block text-sm font-medium text-gray-700">
                    Έτη Εμπειρίας
                  </label>
                  <div className="mt-1">
                    <input
                      id="yearsOfExperience"
                      name="yearsOfExperience"
                      type="number"
                      min="0"
                      max="60"
                      value={formData.yearsOfExperience}
                      onChange={handleInputChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Πεδία Ενδιαφέροντος {formData.medicalRole !== 'external_collaborator' ? '*' : '(προαιρετικό)'}
                  </label>
                  {formData.medicalRole === 'external_collaborator' && (
                    <p className="text-sm text-gray-600 mb-3">
                      Για εξωτερικούς συνεργάτες, η επιλογή πεδίων ενδιαφέροντος είναι προαιρετική.
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    {AREAS_OF_INTEREST.map((area) => (
                      <label key={area} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.areasOfInterest.includes(area)}
                          onChange={() => handleAreaOfInterestChange(area)}
                          className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                            errors.areasOfInterest ? 'border-red-300' : ''
                          }`}
                        />
                        <span className="ml-2 text-sm text-gray-700">{area}</span>
                      </label>
                    ))}
                  </div>
                  {errors.areasOfInterest && (
                    <p className="mt-2 text-sm text-red-600">{errors.areasOfInterest}</p>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    id="agreesToTerms"
                    name="agreesToTerms"
                    type="checkbox"
                    checked={formData.agreesToTerms}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="agreesToTerms" className="ml-2 block text-sm text-gray-900">
                    Αποδέχομαι τους{' '}
                    <Link to="/terms" className="text-blue-600 hover:text-blue-500">
                      όρους χρήσης
                    </Link>{' '}
                    και την{' '}
                    <Link to="/privacy" className="text-blue-600 hover:text-blue-500">
                      πολιτική προστασίας προσωπικών δεδομένων
                    </Link> *
                  </label>
                </div>
                {errors.agreesToTerms && (
                  <p className="text-sm text-red-600">{errors.agreesToTerms}</p>
                )}

                {errors.general && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="text-sm text-red-700">{errors.general}</div>
                  </div>
                )}

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Πίσω
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {isLoading ? <LoadingSpinner size="sm" /> : 'Εγγραφή'}
                  </button>
                </div>
              </>
            )}
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Έχετε ήδη λογαριασμό;</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/login"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-blue-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Σύνδεση
              </Link>
            </div>
          </div>

          {/* Mobile optimization notice */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Βελτιστοποιημένο για κινητά και tablet
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}